<template>
  <ExpandableTable
    item-key="rating_option"
    data-item-category-key="rating_option"
    :category-headers="headers"
    :category-items="computedCategoryItems"
    :expanded-table-headers="expandHeaders"
    :data-items="routes"
    :extract-category-data-items="getDataItemsForCategory"
    :xlsx-filename="computedXlsxFilename"
  >

  </ExpandableTable>
</template>

<script>
import ExpandableTable from './ExpandableTable.vue';

export default {
  name: 'CustomerRouteExpandableTable',
  components: { ExpandableTable },

  props: {
    routeMapCategories: {
      type: Array,
      default: () => [],
    },

    routes: {
      type: Array,
      default: () => [],
    },

    geotagsWithoutRoutes: {
      type: Array,
      default: () => [],
    },

    customerName: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      headers: [
        { text: 'Category', value: 'category', calc: (item) => `${item.manual_name} ${item.route_based_name ? `(${item.route_based_name})` : ''}` },
        { text: '#Destinations ', value: 'routemap_amount', calc: (item) => item.routemap_amount },
        {
          text: 'Flights/Month', value: 'flights_month_amount', is_page_only: true, calc: (item) => item.flights_month_amount,
        },
        {
          text: '#Guides', value: 'tripbits_amount_export', is_export_only: true, calc: (item) => item.tripbits_amount_export,
        },
        {
          text: '#flights w/ guides',
          value: 'flights_guide_month_amount_export',
          is_export_only: true,
          calc: (item) => item.flights_guide_month_amount_export,
        },
        {
          text: '#flights total', value: 'flights_month_amount', is_export_only: true, calc: (item) => item.flights_month_amount,
        },
        {
          text: '#Guides', value: 'tripbits_amount', is_page_only: true, calc: (item) => item.tripbits_amount,
        },
        {
          text: 'Coverage', value: 'coverage', is_page_only: true, calc: (item) => `${item.coverage}%`,
        },
        {
          text: 'Coverage', value: 'coverage_export', is_export_only: true, calc: (item) => `${item.coverage_export}%`,
        },
      ],
      expandHeaders: [
        {
          text: 'Destination', value: 'route_name', isAdditional: false, calc: (item) => item.route_name,
        },
        {
          text: 'Destination code', value: 'iata_icao', isAdditional: false, calc: (item) => item.iata_icao.join(', '),
        },
        {
          text: 'Route flights (month avg)', value: 'route_flights_amount', isAdditional: false, calc: (item) => item.route_flights_amount,
        },
        {
          text: 'Weight of a destination',
          value: 'destination_weight',
          isAdditional: false,
          calc: (item) => `${Math.round(item.destination_weight)}%`,
        },
        {
          text: 'Cumulative routes', value: 'cumulative_routes', isAdditional: false, calc: (item) => item.cumulative_routes,
        },
        {
          text: 'Cumulative weight of a destination',
          value: 'destination_cumulative_weight',
          isAdditional: false,
          calc: (item) => `${Math.round(item.destination_cumulative_weight)}%`,
        },
        {
          text: 'Number POI\'s', value: 'pois_amount', isAdditional: false, calc: (item) => item.pois_amount,
        },
        {
          text: 'Number of Reels', value: 'reels_amount', isAdditional: false, calc: (item) => item.reels_amount,
        },
        {
          text: 'Geotags', value: 'geotags', isAdditional: true, calc: (item) => item.geotags.join(', '),
        },
        {
          text: 'Placenames IDs', value: 'pn_ids', isAdditional: true, calc: (item) => item.pn_ids.join(', '),
        },
        {
          text: 'Tripbits ID', value: 'tb_ids', isAdditional: true, calc: (item) => item.tb_ids.join(', '),
        },
      ],
    };
  },

  methods: {
    getDataItemsForCategory(categoryName) {
      const routes = categoryName === 'total'
        ? this.routes
        : this.routes
          .filter((el) => el.rating_list.includes(categoryName))
          .sort((a, b) => b.route_flights_amount - a.route_flights_amount);

      const totalRouteNumber = routes.reduce(
        (accum, el) => accum + parseInt(el.route_flights_amount, 10),
        0,
      );

      const updatedRoutes = [];

      let cumulativeRouteNumber = 0;
      for (let i = 0; i < routes.length; i += 1) {
        cumulativeRouteNumber = i === 0
          ? routes[i].route_flights_amount
          : cumulativeRouteNumber + routes[i].route_flights_amount;

        updatedRoutes.push({
          ...routes[i],
          destination_weight: totalRouteNumber
            ? (100 * routes[i].route_flights_amount) / totalRouteNumber
            : 0,
          cumulative_routes: cumulativeRouteNumber,
          destination_cumulative_weight: totalRouteNumber
            ? (100 * cumulativeRouteNumber) / totalRouteNumber
            : 0,
        });
      }

      return updatedRoutes;
    },
  },

  computed: {
    computedXlsxFilename() {
      return `${this.customerName}_route_map_${Date.now()}.xlsx`;
    },

    noFlightsCategory() {
      const tripbitsAmount = this.geotagsWithoutRoutes
        .reduce((acc, el) => (el.tb_ids.length && el.is_live[0] ? acc + 1 : acc), 0);
      const routemapAmount = this.geotagsWithoutRoutes
        .reduce((acc, el) => (el.pn_ids.length ? acc + 1 : acc), 0);
      const flightMonthAmount = this.geotagsWithoutRoutes
        .reduce((acc, el) => acc + el.route_flights_amount, 0);

      const tripbitsAmountExport = this.geotagsWithoutRoutes
        .reduce((acc, el) => (el.tb_ids.length > 2 && el.is_live[0] ? acc + 1 : acc), 0);
      const flightGuideMonthAmountExport = this.geotagsWithoutRoutes
        .reduce((acc, el) => (el.tb_ids.length > 2 ? acc + el.route_flights_amount : acc), 0);

      return ({
        rating_option: 'no_flights',
        manual_name: 'No flights',
        route_based_name: '',
        tripbits_live: 0,
        tripbits_amount: tripbitsAmount,
        tripbits_multi: 0,
        routemap_amount: routemapAmount,
        coverage: 0,
        flights_month_amount: flightMonthAmount,
        tripbits_amount_export: tripbitsAmountExport,
        coverage_export: flightMonthAmount === 0
          ? 0
          : ((100 * flightGuideMonthAmountExport) / flightMonthAmount).toFixed(1),
        flights_guide_month_amount_export: flightGuideMonthAmountExport,
      });
    },

    totalCategory() {
      const selectedRoutes = this.routes
        .filter((el) => el.is_selected);
      const tripbitsAmount = selectedRoutes
        .reduce(
          (acc, el) => (el.tb_ids.length && el.is_live.reduce(
            (sum, el2) => sum || el2,
            false,
          ) ? acc + 1 : acc),
          0,
        );
      const routemapAmount = selectedRoutes
        .reduce((acc, el) => (el.pn_ids.length ? acc + 1 : acc), 0);
      const flightMonthAmount = selectedRoutes
        .reduce((acc, el) => acc + el.route_flights_amount, 0);

      const tripbitsAmountExport = selectedRoutes
        .reduce((acc, el) => (el.tb_ids.length > 2 && el.is_live[0] ? acc + 1 : acc), 0);
      const flightGuideMonthAmountExport = selectedRoutes
        .reduce((acc, el) => (el.tb_ids.length > 2 ? acc + el.route_flights_amount : acc), 0);

      return ({
        rating_option: 'total',
        manual_name: 'Total',
        route_based_name: '',
        tripbits_live: 0,
        tripbits_amount: tripbitsAmount,
        tripbits_multi: 0,
        routemap_amount: routemapAmount,
        coverage: routemapAmount === 0
          ? 0
          : ((100 * tripbitsAmount) / routemapAmount).toFixed(1),
        flights_month_amount: flightMonthAmount,
        tripbits_amount_export: tripbitsAmountExport,
        coverage_export: flightMonthAmount === 0
          ? 0
          : ((100 * flightGuideMonthAmountExport) / flightMonthAmount).toFixed(1),
        flights_guide_month_amount_export: flightGuideMonthAmountExport,
      });
    },

    computedCategoryItems() {
      return [
        ...this.routeMapCategories,
        this.noFlightsCategory,
        this.totalCategory,
      ];
    },
  },
};
</script>

<style scoped>

</style>
